import type {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

/** Inline multimodal path — must match encamp-scout-connection SCOUT_NATIVE_MAX_PDF_BYTES */
export const SCOUT_NATIVE_MAX_PDF_BYTES = 30 * 1024 * 1024;

/** Large-PDF extraction path — must match encamp-scout-connection SCOUT_LARGE_PDF_MAX_BYTES */
export const SCOUT_LARGE_PDF_MAX_BYTES = 200 * 1024 * 1024;

export const SCOUT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const SCOUT_MAX_TEXT_BYTES = 200 * 1024;
export const SCOUT_MAX_ATTACHMENTS = 5;

const toMiB = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export function validateScoutAttachmentFile(file: File): string | undefined {
  const mimeType = file.type.toLowerCase();

  if (mimeType === "application/pdf") {
    if (file.size > SCOUT_LARGE_PDF_MAX_BYTES) {
      return `File is larger than 200 MB (${toMiB(file.size)}) and is not supported.`;
    }
    return undefined;
  }

  if (
    mimeType === "image/png" ||
    mimeType === "image/jpeg" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp"
  ) {
    if (file.size > SCOUT_MAX_IMAGE_BYTES) {
      return `File is larger than 5 MB (${toMiB(file.size)}).`;
    }
    return undefined;
  }

  if (mimeType === "text/plain") {
    if (file.size > SCOUT_MAX_TEXT_BYTES) {
      return "File is larger than 200 KB.";
    }
    return undefined;
  }

  return "File type is unsupported. Scout currently supports PDFs, images, and plain text.";
}

export function userFriendlyScoutMediaType(mediaType: string): string {
  switch (mediaType) {
    case "application/pdf":
      return "PDF";
    case "image/png":
      return "PNG";
    case "image/jpeg":
      return "JPEG";
    default:
      return mediaType;
  }
}

export function formatScoutFileSecondaryText(input: {
  mediaType: string;
  ingestionMode?: ScoutPrivateDocumentIngestionMode | string;
  extractionStatus?: ScoutPrivateDocumentExtractionStatus | string;
  extractionProgress?: {
    pagesCompleted: number;
    pagesTotal?: number | null;
  } | null;
}): string {
  const typeLabel = userFriendlyScoutMediaType(input.mediaType);

  if (input.ingestionMode !== "LARGE_PDF_EXTRACTION") {
    return typeLabel;
  }

  if (input.extractionStatus === "PREVIEW_RUNNING") {
    return `${typeLabel} · Scanning first pages…`;
  }

  if (input.extractionStatus === "AWAITING_CONFIRMATION") {
    return `${typeLabel} · Confirm to start full analysis`;
  }

  if (input.extractionStatus === "FULL_ANALYSIS_DECLINED") {
    return `${typeLabel} · Using preview only`;
  }

  if (input.extractionStatus === "PENDING" || input.extractionStatus === "RUNNING") {
    const progress = input.extractionProgress;
    if (progress?.pagesTotal && progress.pagesTotal > 0) {
      return `${typeLabel} · Analyzing page ${progress.pagesCompleted} of ${progress.pagesTotal}`;
    }
    return `${typeLabel} · Full analysis in progress…`;
  }

  if (input.extractionStatus === "FAILED") {
    return `${typeLabel} · Analysis failed`;
  }

  if (input.extractionStatus === "NOT_ATTEMPTED") {
    return `${typeLabel} · Large file`;
  }

  return typeLabel;
}
